'use client';

import { create } from 'zustand';
import { viewMap } from '@/views';
import { fetchDetailsByPaymentId } from '@/api/payment';
import {
  ApiFunctionResponse,
  EmiPlans,
  LENDER_DETAILS_TYPE,
  PaymentFetchResponse,
} from '@/api/types';
import { IUser } from '@/@types/globals';
import { updatePaymentDataInAnalytics } from '@/analytics/utils';

export interface IGlobalStore {
  activeView: keyof typeof viewMap;
  user: IUser;
  setUser: (viewName: Partial<IUser>) => void;
  setActiveView: (viewName: keyof typeof viewMap) => void;
  emiPlans: EmiPlans[];
  selectedTenure: EmiPlans | null;
  setEmiPlans: (plans: EmiPlans[]) => void;
  setSelectedTenure: (plan: EmiPlans) => void;
  fetchPaymentDetails: () => void;
  xDataToken: string | null;
  userAccessToken: string | null;
  merchantKey: string | null;
  setXDataToken: (token: string) => void;
  setUserAccessToken: (token: string) => void;
  setMerchantKey: (key: string) => void;
  paymentDetails: {
    loading: boolean;
    data: null | PaymentFetchResponse;
    error: null | string;
  };
  lenderDetails: LENDER_DETAILS_TYPE[];
  setLenderDetails: (details: LENDER_DETAILS_TYPE[]) => void;
  eMandateUrl: string | null;
  digiLockerUrl: string | null;
  approvedCreditLimit: number | null;
  setEMandateUrl: (url: string) => void;
  setDigiLockerUrl: (url: string) => void;
  setApprovedCreditLimit: (limit: number) => void;
  repaymentId: string | null;
  setRepaymentId: (id: string) => void;
}

export const useGlobalStore = create<IGlobalStore>((set) => ({
  activeView: 'LANDING',
  user: {
    contact: '',
  },
  setActiveView: (viewName): void => set({ activeView: viewName }),
  setUser: (newUser): void => set((state) => ({ user: { ...state.user, ...newUser } })),
  emiPlans: [],
  selectedTenure: null,
  setEmiPlans: (plans): void => set({ emiPlans: plans }),
  setSelectedTenure: (plan): void => set({ selectedTenure: plan }),
  paymentDetails: {
    loading: true,
    data: null,
    error: null,
  },
  fetchPaymentDetails: async (): Promise<PaymentFetchResponse | null> => {
    try {
      set({
        paymentDetails: {
          loading: true,
          error: null,
          data: null,
        },
      });
      const response = await fetchDetailsByPaymentId();
      if (response.success && response.data) {
        const { data } = response;
        // set meta for payment details
        updatePaymentDataInAnalytics(data);
        set({
          paymentDetails: {
            loading: false,
            error: null,
            data,
          },
          user: {
            contact: data.contact.mobile,
          },
        });
        return response.data;
      }

      if (response.error) {
        set({
          paymentDetails: {
            loading: false,
            error: response.error,
            data: null,
          },
        });
      }
      return null;
    } catch (error: unknown) {
      set({
        paymentDetails: {
          loading: false,
          error: 'Loading failed due to a technical issue',
          data: null,
        },
      });
      return null;
    }
  },
  userAccessToken: null,
  merchantKey: null,
  setUserAccessToken: (token): void => set({ userAccessToken: token }),
  setMerchantKey: (key): void => set({ merchantKey: key }),
  xDataToken: null,
  setXDataToken: (token): void => set({ xDataToken: token }),
  lenderDetails: [],
  setLenderDetails: (lenders): void => set({ lenderDetails: lenders }),
  eMandateUrl: null,
  setEMandateUrl: (url: string): void => set({ eMandateUrl: url }),
  digiLockerUrl: null,
  setDigiLockerUrl: (url: string): void => set({ digiLockerUrl: url }),
  setRepaymentId: (id: string): void => set({ repaymentId: id }),
  repaymentId: '',
  approvedCreditLimit: null,
  setApprovedCreditLimit: (limit: number): void => set({ approvedCreditLimit: limit }),
}));
